// Full column definitions for the three IM reference sub-grids (Item Master, Item/Lot, Location Master),
// reproducing the Delphi query field sets (frmIMM0049 / frmIMM0034 / frmIMM0051). The columns the legacy
// grids display are visible; every other query field is added visible:false for the column chooser.
// dataFields are PascalCase — these IM master endpoints (api/im/Item|Lot|Location) serialize PascalCase.
// Fields the reused DTO does not expose (e.g. IMLot's item-level joined columns) are omitted.

import { status as LocationStatuses, locationTypes, LotStatus } from "../../../const/Lookup";
import { ITEM_STATUSES } from "./codeLists";

const money = { type: "fixedPoint", precision: 2 };

const txt = (dataField, caption, visible = false) => ({ dataField, caption, visible });
// Coded field rendered as a descriptive dropdown. `lk` = shared const/Lookup {key,value} lists;
// `lkc` = codeLists {code,label} lists.
const lk = (dataField, caption, dataSource, visible = false) => ({ dataField, caption, visible, lookup: { dataSource, valueExpr: "key", displayExpr: "value" } });
const lkc = (dataField, caption, dataSource, visible = false) => ({ dataField, caption, visible, lookup: { dataSource, valueExpr: "code", displayExpr: "label" } });
const num = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "number", format: money });
const dt = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "date", format: "MM/dd/yyyy" });
const dtm = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "datetime", format: "MM/dd/yyyy hh:mm a" });
const key = (dataField) => ({ dataField, visible: false, showInColumnChooser: false });

// User-defined column families (all hidden). kind: 'txt' | 'date' | 'num'.
const udf = (fieldPrefix, labelSuffix, kind, count = 10) =>
  Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const f = `${fieldPrefix}${n}`;
    const label = `${kind === "date" ? "Date" : kind === "num" ? "Quantity" : fieldPrefix.includes("Reference") ? "Reference" : "Code"} ${n}${labelSuffix}`;
    return kind === "date" ? dt(f, label) : kind === "num" ? num(f, label) : txt(f, label);
  });

// ---- Item Master (frmIMM0049.qryIMItem → ItemDto) ----
export const ITEM_COLUMNS = [
  key("RowId"),
  txt("Record", "Record #", true),
  txt("ItemCode", "Item", true),
  txt("Description1", "Description - 1", true),
  txt("Description2", "Description - 2", true),
  txt("ClassCode", "Class", true),
  txt("SubClassCode", "Sub Class", true),
  lkc("Status", "Status", ITEM_STATUSES),
  txt("ItemType", "Item Type"),
  txt("OemManufacturerCode", "OEM Code"),
  txt("AbcCode", "ABC Code"),
  txt("StatusReasonCode", "Reason Code"),
  txt("TaxStatus", "Tax Status"),
  txt("LocationType", "Location Type"),
  txt("PhysicalCountCode", "Physical Count Code"),
  txt("FreightClassCode", "Freight Class Code"),
  txt("InventoryUnitUom", "UOM"),
  txt("PiecesPerUnitUom", "Pieces per Unit UOM"),
  num("PiecesPerUnitQuantity", "Pieces per Unit Quant"),
  txt("UnitsPerPackageUom", "Units per Package UOM"),
  num("UnitsPerPackageQuantity", "Units per Package Quant"),
  txt("ArTradeDiscountFlag", "AR Trade Disc Flag"),
  txt("ArTermsDiscountFlag", "AR Terms Disc Flag"),
  txt("PackagesPerShippingUnitUom", "Packages per Ship Unit UOM"),
  num("PackagesPerShippingUnitQty", "Packages per Ship Unit Quant"),
  num("SaleIncrementQuantity", "Sales Inc"),
  txt("UnitVolumeUom", "Unit Volume UOM"),
  num("MinimumSalesQuantity", "Minimum Qty"),
  num("UnitVolumeQuantity", "Unit Vol Quant"),
  txt("UnitAreaUom", "Unit Area UOM"),
  num("UnitAreaQuantity", "Unit Area Quant"),
  txt("UnitValueUom", "Unit Value UOM"),
  num("UnitValueQuantity", "Unit Value Quant"),
  txt("WeightBasisFlag", "Weight Basis Flag"),
  txt("NetWeightUom", "Net Weight UOM"),
  num("NetWeightQuantity", "Net Weight Qty"),
  txt("GrossWeightUom", "Gross Weight UOM"),
  num("GrossWeightQuantity", "Gross Weight Qty"),
  txt("VendorItemCode", "Vendor Item Code"),
  txt("ReceiptUomDefault", "Rec UOM Default"),
  txt("ReceiptUomMultiple", "Rec UOM Multi"),
  txt("ReceiptUomAlternative", "Rec UOM Alt"),
  num("ReceiptPackSize", "Rec Pack Size"),
  txt("ShipmentUomDefault", "Ship UOM Default"),
  txt("ShipmentUomMultiple", "Ship UOM Multi"),
  txt("ShipmentUomAlternative", "Ship UOM Alt"),
  num("ShipmentPackSize", "Ship Pack Size"),
  txt("ShipSelection", "Ship Select"),
  num("StackHeight", "Stack Height"),
  num("PalletPatternRow", "Pallet Pattern Row"),
  num("PalletPatternColumn", "Pallet Pattern Column"),
  num("PalletPatternHeight", "Pallet Pattern Height"),
  txt("PalletSizeCode", "Pallet Size Code"),
  txt("BoxCode", "Box Code"),
  txt("HazardousFlag", "Hazard Flag"),
  txt("CareInstructions", "Care Instruct"),
  txt("SpecificationCode", "Spec Code"),
  txt("ItemSupcCode", "Items UPC Code"),
  txt("BomExplosionFlag", "BOM Exp Flag"),
  txt("MiscelleanousFeeFlag", "Misc Fee Flag"),
  txt("StorageLocationType", "Storage Loc Type"),
  txt("StorageLocationPriorityFlag", "Storage Loc Prior Flag"),
  txt("SystemCode", "System Code"),
  txt("AssemblyCode", "Assembly Code"),
  txt("PartCode", "Part Code"),
  txt("EmCostBucketNumber", "EM Cost Bucket No"),
  txt("TlFuelTaxFlag", "TL Fuel Tax Flag"),
  txt("TlFuelTaxCode", "TL Fuel Tax Code"),
  dt("DateIntroduced", "Date Intro"),
  dt("DateAvailable", "Date Avail"),
  dt("DateExpiration", "Date Exp"),
  dt("StorageBillingStartDate", "Storage Bill Start Date"),
  dt("StorageBillingEndDate", "Storage Bill End Date"),
  txt("ItemSourceFlag", "Item Source Flag"),
  txt("ReorderMethodFlag", "ReOrder Method Flag"),
  num("ReorderInventoryMinimumQty", "ReOrder Inv Min Qty"),
  num("ReorderInventoryMaximumQty", "ReOrder Inv Max Qty"),
  num("ReorderMinimumDaysOnHand", "ReOrder Min Days OnHand"),
  num("ReorderMaximumDaysOnHand", "ReOrder Max Days OnHand"),
  num("ReorderQuantityEoqCalculated", "ReOrder Quant EOQ Calc"),
  num("ReorderQuantityEntered", "ReOrder Quant Entered"),
  num("ReorderQuantityOrderMultiple", "ReOrder Quant Order Multi"),
  num("ReorderSafetyQuantity", "ReOrder Safety Quant"),
  num("ReorderLeadTime", "ReOrder Lead Time"),
  txt("CostBasisMethodFlag", "Cost Basis"),
  num("AverageCost", "Average Cost"),
  num("StandardCost", "Standard Cost"),
  num("LastCost", "Last Cost"),
  num("CorePrice", "Core Price"),
  num("MaximumPriceLevelForDiscount", "Max Price Level for Disc"),
  dt("AnniversaryBillingDate", "Anniver Bill Date"),
  num("AnniversaryBillingQuantity", "Anniver Bill Quant"),
  num("WarrantyMonths", "Warranty Months"),
  num("WarrantyMeterMiles", "Warranty Meter Miles"),
  num("WarrantyMeterHours", "Warranty Meter Hours"),
  txt("WarrantyFlag", "Warranty Flag"),
  txt("WarrantyPeriodTypeFlag", "Warranty Period Type"),
  ...udf("UserDefinedCode", " - Item", "txt"),
  ...udf("UserDefinedReference", " - Item", "txt"),
  ...udf("UserDefinedDate", " - Item", "date"),
  ...udf("UserDefinedQuantity", " - Item", "num"),
  dtm("CreationDateTime", "Creation Date"),
  txt("CreationUserId", "Created By"),
  dtm("LastUpdateDateTime", "Last Updated"),
  txt("LastUpdateUserId", "Last Updated By"),
  txt("IdentityId", "Warehouse"),
  num("Available", "Available"),
  num("NetAvailable", "Net Available"),
  num("OnHand", "On Hand"),
  num("OutBackOrder", "Back Order - Out"),
  num("InBackOrder", "Back Order - In"),
  num("PriceLevelA", "Price Level A"),
  num("PriceLevelB", "Price Level B"),
  num("PriceLevelC", "Price Level C"),
  num("PriceLevelD", "Price Level D"),
  num("PriceLevelE", "Price Level E"),
];

// ---- Item/Lot (frmIMM0034.qryIMLot → LotDto) ----
// The legacy grid also shows item-level columns (Item Description, Item inventory, Item UDFs), but the
// reused GetLots/LotDto only exposes the item CODE, so those item-level columns are omitted here.
export const LOT_COLUMNS = [
  key("RowId"),
  txt("ItemCode", "Item", true),
  txt("LotCode", "Lot", true),
  txt("Description1", "Lot Description - 1", true),
  num("NetAvailable", "Lot Net Available", true),
  txt("Record", "Record #"),
  txt("Description2", "Lot Description - 2"),
  lk("Status", "Lot Status", LotStatus),
  txt("ClassCode", "Lot Class"),
  txt("SubClassCode", "Lot Subclass"),
  num("OnHand", "Lot On-Hand"),
  num("InReceiving", "Lot In-Receiving"),
  num("OutShipped", "Lot In-Shipping"),
  num("OutUnavailable", "Lot Unavailable"),
  num("Available", "Lot Available"),
  num("InOrdered", "Lot Pending Rec"),
  num("OutOrdered", "Lot Pending Ship"),
  num("InBackOrder", "Lot BO-Receiving"),
  num("OutBackOrder", "Lot BO-Shipping"),
  num("InFuture", "Lot Future Rec"),
  num("OutFuture", "Lot Future Ship"),
  txt("InventoryUnitUom", "UOM"),
  txt("PiecesPerUnitUom", "Lot - Pieces/Unit UOM"),
  num("PiecesPerUnitQuantity", "Lot - Pieces/Unit Qty"),
  txt("UnitsPerPackageUom", "Lot - Units/Package UOM"),
  num("UnitsPerPackageQuantity", "Lot - Units/Package Qty"),
  num("AverageCost", "Lot Avg Cost"),
  num("StandardCost", "Lot Std Cost"),
  num("LastCost", "Lot Last Cost"),
  dt("DateIntroduced", "Lot Introduced Date"),
  dt("DateAvailable", "Lot Available Date"),
  dt("DateExpiration", "Lot Expiration Date"),
  dt("DateLastReceipt", "Last Receipt Date (Lot)"),
  num("OversellPercentage", "Lot Oversell Percentage"),
  num("SaleMaxQuantity", "Sale Max Qty"),
  ...udf("UserDefinedCode", " - Lot", "txt"),
  ...udf("UserDefinedReference", " - Lot", "txt"),
  ...udf("UserDefinedDate", " - Lot", "date"),
  ...udf("UserDefinedQuantity", " - Lot", "num"),
  num("PriceLevelA", "Price Level A"),
  num("PriceLevelB", "Price Level B"),
  num("PriceLevelC", "Price Level C"),
  num("PriceLevelD", "Price Level D"),
  num("PriceLevelE", "Price Level E"),
  dtm("CreationDateTime", "Creation Date"),
  txt("CreationUserId", "Created By"),
  dtm("LastUpdateDateTime", "Last Updated"),
  txt("LastUpdateUserId", "Last Updated By"),
];

// ---- Location Master (frmIMM0051.qryIMLocation → LocationDto) ----
export const LOCATION_COLUMNS = [
  key("RowId"),
  txt("IdentityId", "Warehouse", true),
  txt("LocationCode", "Location", true),
  txt("Description", "Description", true),
  txt("ClassCode", "Class", true),
  txt("SubClassCode", "Sub Class", true),
  txt("Dimensions", "Dimensions", true),
  num("Height", "Height", true),
  num("Width", "Width", true),
  num("Depth", "Depth", true),
  txt("Record", "Record #"),
  lk("Status", "Status", LocationStatuses),
  lk("LocationType", "Location Type", locationTypes),
  txt("Module", "Module"),
  num("CapacityUnits", "Capacity Units"),
  txt("CapacityLimitFlag", "Capacity Limit Flag"),
  num("CapacityPieces", "Capacity Pieces"),
  txt("CapacityPiecesLimitFlag", "Capacity Pieces Limit Flag"),
  num("CapacityContainerUnits", "Capacity Container Units"),
  txt("CapacityContainerLimitFlag", "Capacity Container Limit Flag"),
  num("CapacityShippingUnits", "Capacity Shipping Units"),
  txt("CapacityShippingUnitsLimitFlag", "Capacity Shipping Units Limit Flag"),
  num("CapacityGrossWeight", "Capacity Gross Weight"),
  txt("CapacityGrossWeightLimitFlag", "Capacity Gross Weight Limit Flag"),
  num("CapacityNetWeight", "Capacity Net Weight"),
  txt("CapacityNetWeightLimitFlag", "Capacity Net Weight Limit Flag"),
  num("CapacityArea", "Capacity Area"),
  txt("CapacityAreaLimitFlag", "Capacity Area Limit Flag"),
  num("CapacityVolume", "Capacity Volume"),
  txt("CapacityVolumeLimitFlag", "Capacity Volume Limit Flag"),
  num("MinimumUnits", "Min Units"),
  num("MaximumUnits", "Maximum Units"),
  num("Priority", "Priority"),
  txt("ProfileCode", "Profile Code"),
  txt("ProfileDescription", "Profile Description"),
  ...udf("UserDefinedCode", " - Location", "txt"),
  ...udf("UserDefinedReference", " - Location", "txt"),
  ...udf("UserDefinedDate", " - Location", "date"),
  ...udf("UserDefinedQuantity", " - Location", "num"),
  dtm("CreationDateTime", "Creation Date"),
  txt("CreationUserId", "Created By"),
  dtm("LastUpdatedDateTime", "Last Updated"),
  txt("LastUpdateUserId", "Last Updated By"),
];
